/**
 * Channel-agnostic chat message contract.
 *
 * The server's conversation engine only ever produces these semantic message types.
 * Each channel renders them its own way:
 *   - web:      rich bubbles, chips, cards, and widgets that open as an in-chat "secure page" (sheet)
 *   - whatsapp: text / reply buttons (<=3) / list messages (<=10) / WhatsApp Flows for widgets
 *
 * Keep content semantic (what it means), never visual (how it looks).
 */

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** Icon keys understood by every channel. Web maps them to lucide icons; WhatsApp to emoji. */
export type IconName =
  | 'file-text'
  | 'calendar'
  | 'id-card'
  | 'search'
  | 'message-circle'
  | 'receipt'
  | 'landmark'
  | 'building'
  | 'shield-check'
  | 'upload'
  | 'pencil'
  | 'camera'
  | 'flask'
  | 'check-circle'
  | 'alert-triangle'
  | 'clock'
  | 'credit-card'
  | 'lock'
  | 'user'
  | 'plus'
  | 'car'
  | 'plane'
  | 'home'
  | 'indian-rupee'
  | 'bell'
  | 'list'
  | 'refresh'
  | 'undo'
  | 'x-circle'
  | 'log-out'
  | 'briefcase'
  | 'fingerprint'
  | 'file-check'
  | 'sparkles'
  | 'zap'
  | 'help-circle'
  | 'share';

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

export interface Choice {
  /** Stable value sent back to the server when tapped. */
  id: string;
  /** Short label (WhatsApp button titles are capped at 20 chars; list rows at 24). */
  label: string;
  /** Secondary line, shown in list/cards layouts. */
  description?: string;
  icon?: IconName;
  /** Small pill, e.g. "Live", "Coming soon", "Overdue". */
  badge?: string;
  tone?: Tone;
  disabled?: boolean;
}

export interface Field {
  label: string;
  value: string;
  tone?: Tone;
  /** Render value bold / larger (e.g. totals). */
  emphasis?: boolean;
  /** Muted helper text under the row. */
  hint?: string;
  /** A value people copy (a bank detail): the copy button's label, e.g. "Copy account number". */
  copy?: string;
  /** Digits and codes: a fixed-width face (an account number, an IFSC). */
  mono?: boolean;
  /** One of the app's own pages for this row, e.g. a filing's receipt ('/filings/<id>'): its label opens it. */
  path?: string;
}

export interface Section {
  title?: string;
  /** A muted note at the title's right, e.g. "From GSTR-1 · locked". */
  aside?: string;
  asideIcon?: IconName;
  fields: Field[];
}

export interface Link {
  label: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Bot message content
// ---------------------------------------------------------------------------

/** The exact action offered by this message, and when that offer expires. */
export interface ProposalCard {
  proposalId?: string;
  proposalExpiresAt?: string;
}

/** Where an answer's figures or rule came from: shown under it (next chat, N8a). */
export interface AnswerSource {
  label: string;
  url?: string;
}

/** Markdown-lite: **bold**, _italic_, `code`, [label](url), "- " bullet lines, blank-line paragraphs. */
export interface TextContent extends ProposalCard {
  source?: AnswerSource;
  type: 'text';
  text: string;
  /** A large first line, e.g. "Namaste!" (WhatsApp: a bold first line). */
  title?: string;
  tone?: Tone;
}

export interface ChoicesContent extends ProposalCard {
  type: 'choices';
  text: string;
  choices: Choice[];
  /**
   * chips = quick-reply pills under the bubble
   * list  = vertical menu rows (icon, label, description, badge)
   * cards = grid of tiles (used for the home menu)
   */
  layout?: 'chips' | 'list' | 'cards';
  /** Muted line under the options, e.g. "Or just type what you need". */
  hint?: string;
}

export interface CardContent extends ProposalCard {
  source?: AnswerSource;
  type: 'card';
  title: string;
  subtitle?: string;
  icon?: IconName;
  tone?: Tone;
  sections: Section[];
  /**
   * A callout at the foot of the card, e.g. a row that needs fixing (bold `title`, then `text`). `neutral`
   * quotes someone's own words as they wrote them (an owner's comment), with no icon.
   */
  note?: { tone?: Tone; title?: string; text: string };
  footer?: string;
  /** One of the app's own pages, e.g. { label: 'Open People', path: '/businesses/<id>/people' }. */
  link?: { label: string; path: string };
  /**
   * An official website to do something on, opened in a new tab (https only), e.g. { label: 'Open gst.gov.in',
   * url: 'https://www.gst.gov.in' }: a challan made on the GST portal (step 2).
   */
  openUrl?: { label: string; url: string };
  /** Optional buttons on the card. Makes the message interactive. */
  actions?: Choice[];
  /** inline (default): small buttons in a row; stack: full-width buttons, one under another. */
  actionsLayout?: 'inline' | 'stack';
  /** Muted line under the card, e.g. "Or fix it in your file and upload it again." */
  hint?: string;
  /** What it follows, e.g. "approval:<id>", so the server can find it to update in place. */
  ref?: string;
}

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
}

export interface TableContent {
  type: 'table';
  title?: string;
  caption?: string;
  columns: TableColumn[];
  rows: Record<string, string>[];
  /** Totals row. */
  footer?: Record<string, string>;
}

export interface ConfirmContent extends ProposalCard {
  type: 'confirm';
  /** A small line above the title, e.g. "Please confirm". */
  eyebrow?: string;
  title: string;
  /** Under the title, e.g. "Sharma Electronics · 29AAKPS1234Q1Z0". */
  subtitle?: string;
  text?: string;
  sections: Section[];
  /** Amber callout shown above the buttons (e.g. "Filed returns cannot be revised"). */
  warning?: string;
  /**
   * A box to tick before the confirm button works (gap 4b: GSTR-1A's declaration that GSTR-3B takes its
   * figures, which the GST portal asks for): the confirm choice is sent only once it's ticked.
   */
  agree?: string;
  confirm: Choice;
  cancel?: Choice;
  secondary?: Choice[];
}

export interface ReceiptContent {
  type: 'receipt';
  title: string;
  /** Under the title, e.g. "Accepted by the GST portal". */
  subtitle?: string;
  /** Primary reference, e.g. { label: 'ARN', value: 'AA290926041857K' }. Rendered monospace with a copy button. */
  reference: { label: string; value: string };
  sections: Section[];
  downloadUrl?: string;
  /** One of the app's own pages about it, e.g. { label: 'See the receipt', path: '/filings/<id>' }. */
  link?: { label: string; path: string };
  tone?: Tone;
}

export type StepState = 'pending' | 'active' | 'done' | 'error';

/** Live progress checklist. The server updates it in place via `message_update`. */
export interface StatusContent {
  type: 'status';
  title?: string;
  steps: { label: string; state: StepState; detail?: string }[];
  /** What it follows, e.g. "import:<id>", so the server can find it to update. */
  ref?: string;
}

export interface WidgetContent {
  type: 'widget';
  /** Shown in the chat bubble above the CTA. */
  text?: string;
  widget: WidgetSpec;
}

export type BotContent =
  | TextContent
  | ChoicesContent
  | CardContent
  | TableContent
  | ConfirmContent
  | ReceiptContent
  | StatusContent
  | WidgetContent;

// ---------------------------------------------------------------------------
// Widgets: mini pages opened *inside* the chat (a sheet on web, a Flow on WhatsApp)
// ---------------------------------------------------------------------------

export interface WidgetBase {
  /** Widget instance id (unique per message). */
  id: string;
  title: string;
  description?: string;
  /** Button label in the chat card that opens the widget, e.g. "Enter OTP". */
  cta: string;
  icon?: IconName;
  /** Trust label in the sheet header, e.g. "Secured by EkApp · GSTN API (Sandbox)". */
  secureLabel?: string;
  /** Open automatically when this message arrives live. */
  autoOpen?: boolean;
  /** Info callout at the top of the sheet. */
  note?: string;
  links?: Link[];
}

export interface OtpWidget extends WidgetBase {
  /** Server-issued code challenge; the value goes only to /codes/:id. */
  challengeId?: string;
  codeFormatError?: string;
  kind: 'otp';
  length: number;
  /** Letters as well as digits (EVC codes); else digits alone. */
  letters?: boolean;
  /** Masked destinations, e.g. ["+91 ******4321", "r****@gmail.com"]. */
  sentTo: string[];
  /** What the OTP authorises, e.g. "Sign GSTR-3B for Aug 2026 with EVC". */
  purpose?: string;
  /** Who signs with it (EVC), e.g. { name: 'Rahul Sharma', detail: 'Authorised signatory · PAN AAKPS••••Q' }. */
  signer?: { name: string; detail: string };
  /** The sheet's button, e.g. "Sign and file" (default: a plain "Submit"). */
  submitLabel?: string;
  /** How the person's message reads once it's sent, e.g. "Signed with EVC" (default: dots). Never the code. */
  submitSummary?: string;
  resendAfterSec?: number;
  expiresInSec?: number;
  attemptsLeft?: number;
  /** Sandbox-only helper, e.g. "Sandbox OTP: 575757". */
  hint?: string;
  /**
   * A test portal (the twin, a GSP's sandbox): the page says its fixed public code (TEST_PORTAL_CODE). A flag,
   * never the code itself, so no code is ever stored with a message.
   */
  testHint?: 'otp' | 'evc';
}

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'currency'
  | 'date'
  | 'month'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'gstin'
  | 'tel'
  | 'email'
  | 'password';

export interface FormField {
  name: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  placeholder?: string;
  help?: string;
  /** For select / radio. */
  options?: { value: string; label: string }[];
  /** number/currency: numeric bounds. date/month: ISO bounds ("2026-08-01" / "2026-08"). */
  min?: number | string;
  max?: number | string;
  step?: number;
  /** Regex source (no slashes) validated client-side and server-side. */
  pattern?: string;
  patternMessage?: string;
  maxLength?: number;
  /** Force upper-case input (GSTIN, PAN, invoice numbers). */
  uppercase?: boolean;
  /** Never echoed into the transcript. */
  sensitive?: boolean;
  defaultValue?: string | number | boolean;
  /** Only show when another field has one of these values. */
  showIf?: { field: string; equals: string | boolean | (string | boolean)[] };
  width?: 'full' | 'half';
}

export interface FormWidget extends WidgetBase {
  /** Server-issued code challenge; the value goes only to /codes/:id. */
  challengeId?: string;
  codeFormatError?: string;
  kind: 'form';
  fields: FormField[];
  submitLabel: string;
  values?: Record<string, string | number | boolean>;
}

export interface UploadWidget extends WidgetBase {
  kind: 'upload';
  /** What the files are for (POST /uploads `purpose`), e.g. "sales_file". */
  purpose: string;
  /** e.g. [".csv", "image/*", "application/pdf"] */
  accept: string[];
  multiple?: boolean;
  maxFiles?: number;
  maxSizeMb?: number;
  /** A file to start from; `templates` lists every format of it, the first the main one. */
  template?: Link;
  templates?: Link[];
  /** The columns a file needs, shown as chips ("Invoice no", "Invoice date"…). */
  columns?: string[];
  /** Something to know before choosing (shown in amber): e.g. only made-up invoices in a test setup. */
  warning?: string;
  submitLabel?: string;
  /** The sheet's own heading and the line under it, e.g. "Add your statement for August" (Phase 16). */
  heading?: string;
  subtitle?: string;
  /** What to do first, under the heading: e.g. where to download a statement. */
  intro?: string;
  /** What to know, each with its icon: a statement's password, and who sees it. */
  notes?: { icon: IconName; text: string }[];
}

/** A detail to copy into a form elsewhere (a bank's), e.g. the account number to pay a challan to. */
export interface CopyField {
  label: string;
  value: string;
  /** The copy button's label, e.g. "Copy account number". */
  copyLabel: string;
  /** Digits and codes: a fixed-width face. */
  mono?: boolean;
}

/**
 * A GST challan to pay at the bank (Phase 11): by NEFT or RTGS, from the bank the challan names, to the
 * Government's GST account at RBI. The sheet shows the exact amount and each detail the bank asks for,
 * to copy; "I've paid" comes back as a `widget` answer (no data) and the chat watches for RBI's word.
 */
export interface PaymentWidget extends WidgetBase {
  kind: 'payment';
  /** Above the amount, e.g. "GST challan (PMT-06) · August 2026". */
  eyebrow: string;
  /** e.g. "Pay exactly, in one payment". */
  amountLabel: string;
  /** Formatted, e.g. "₹21,700". */
  amount: string;
  /** Under it, e.g. "IGST ₹2,700 · CGST ₹9,500 · SGST ₹9,500 · valid until 13 Oct 2026". */
  amountHint?: string;
  /** How to pay it (markdown-lite). */
  instructions: string;
  /** What the bank's form asks for, in its order. */
  details: CopyField[];
  /** Amber: what mustn't change. */
  warning?: string;
  /** Blue, at the foot: where the money goes. */
  footnote?: string;
  /** The main button, e.g. "I've paid", and how the person's message reads once it's tapped. */
  submitLabel: string;
  submitSummary?: string;
  /** Copies every detail at once. */
  copyAll?: { label: string; text: string };
}

/** Embeds a page (iframe) inside the chat, e.g. DigiLocker consent or a hosted guide. */
export interface WebviewWidget extends WidgetBase {
  kind: 'webview';
  url: string;
}

/**
 * Opens one of EkApp's own pages, such as Add a sale, from the chat. Coming back sends a
 * `widget_action` ("back" or what the page did, e.g. "review") so the chat can carry on from there.
 */
export interface PageWidget extends WidgetBase {
  kind: 'page';
  /** The app path, e.g. "/returns/{id}/sales/new". */
  path: string;
}

/**
 * A message for the person to send someone else (gap 4b: a rejected credit note, for the buyer). The sheet shows
 * it; Share hands it to the phone's share sheet (WhatsApp, SMS, email), or copies it where there's none. Nothing
 * comes back to the chat.
 */
export interface ShareWidget extends WidgetBase {
  kind: 'share';
  text: string;
}

export type WidgetSpec =
  | OtpWidget
  | FormWidget
  | UploadWidget
  | PaymentWidget
  | WebviewWidget
  | PageWidget
  | ShareWidget;
export type WidgetKind = WidgetSpec['kind'];

// ---------------------------------------------------------------------------
// User / system message content (as stored in the transcript)
// ---------------------------------------------------------------------------

export interface FileRef {
  id: string;
  name: string;
  size: number;
  mime: string;
}

export interface UserTextContent {
  type: 'user_text';
  text: string;
}

export interface UserChoiceContent {
  type: 'user_choice';
  choiceId: string;
  label: string;
}

/** Echo of a widget submission. Sensitive values (OTP, passwords) are never included. */
export interface UserSubmitContent {
  type: 'user_submit';
  summary: string;
  icon?: IconName;
}

export interface UserFilesContent {
  type: 'user_files';
  files: FileRef[];
}

export interface SystemContent {
  type: 'system';
  text: string;
}

export type UserContent = UserTextContent | UserChoiceContent | UserSubmitContent | UserFilesContent;
export type MessageContent = BotContent | UserContent | SystemContent;

// ---------------------------------------------------------------------------
// Transcript envelope
// ---------------------------------------------------------------------------

export type InteractiveStatus = 'active' | 'answered' | 'expired';

/** Hint for the composer while this (latest) bot message awaits typed input. */
export interface ComposerHint {
  pastedTables?: boolean;
  /** Server-issued code challenge; the value goes only to /codes/:id. */
  challengeId?: string;
  codeFormatError?: string;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
  uppercase?: boolean;
  maxLength?: number;
  /** Hidden input (an OTP): shown as dots and sent only to the code endpoint. */
  secret?: boolean;
  /** What's hidden: a one-time code (the default) or a password (a statement's, Phase 16). */
  secretKind?: 'code' | 'password';
}

export interface ChatMessage {
  id: string;
  /** Monotonic per conversation; used for ordering. */
  seq: number;
  /** Bumped whenever the message is created or updated; SSE event ids and replay use it. */
  rev: number;
  from: 'bot' | 'user' | 'system';
  createdAt: string;
  content: MessageContent;
  /** Set on interactive bot messages (choices, widget, confirm, card with actions). */
  status?: InteractiveStatus;
  /** Choice id the user picked, once answered. */
  selected?: string;
  composer?: ComposerHint;
  /** Echo of ClientEvent.clientId on the user message it produced (for optimistic UI). */
  clientRef?: string;
}

export function isInteractive(content: MessageContent): boolean {
  return (
    content.type === 'choices' ||
    content.type === 'widget' ||
    content.type === 'confirm' ||
    (content.type === 'card' && !!content.actions?.length)
  );
}

/** Test portals' fixed code (the twin, GSP sandboxes), public by design: never a real person's code. */
export const TEST_PORTAL_CODE = '575757';
